import type { QueryClient } from '@tanstack/react-query';

import { policyControllerListBranchesQueryKey } from '../../generated/kubb/kubb-hooks';

function isKubbGetPolicyQueryKey(policyId: string, queryKey: readonly unknown[]) {
  const first = queryKey[0];
  if (typeof first !== 'object' || first === null || !('url' in first) || !('params' in first)) {
    return false;
  }
  const entry = first as { url: string; params: { policyId?: string } };
  return entry.url === '/policies/:policyId' && entry.params?.policyId === policyId;
}

/**
 * Invalidates policy dashboard data for a policy id across:
 * - korint-sdk/react (`useBranchesGetByPolicyIdOptions`, `usePolicyGetByIdOptions`)
 * - Kubb-generated hooks (`policyControllerListBranches`, `policyControllerGetPolicy`)
 *
 * Call after archive, fork branch (unarchiving), or similar mutations so all UIs stay in sync.
 */
export function invalidatePolicyDashboardCache(queryClient: QueryClient, policyId: string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ['policy', 'listBranches', { policyId }] as const }),
    queryClient.invalidateQueries({
      predicate: query => {
        const key = query.queryKey;
        if (!Array.isArray(key) || key.length < 3) return false;
        if (key[0] !== 'policy' || key[1] !== 'getPolicy') return false;
        const params = key[2];
        if (typeof params !== 'object' || params === null || !('policyId' in params)) return false;
        return (params as { policyId: string }).policyId === policyId;
      },
    }),
    queryClient.invalidateQueries({ queryKey: policyControllerListBranchesQueryKey(policyId) }),
    queryClient.invalidateQueries({
      predicate: query => isKubbGetPolicyQueryKey(policyId, query.queryKey),
    }),
  ]);
}
